import { type SAFonts } from '@atoms/FontPicker';
import SlideBackground from '@atoms/new/slideBackground';
import Textarea from '@atoms/new/textarea';
import { useSelectedSlide } from '@components/Presentation/hooks/useSelectedSlide';
import { usePresentationUIStore } from '@components/Presentation/store/presentationUIStore';
import useFonts from '@lib/hooks/useFonts';
import type React from 'react';
import { useState, useRef } from 'react';

interface InteractionWrapperProps {
  children: React.ReactNode;
  headingColor: string;
  bgColor: string;
  title: string;
  updateTitle?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  bgImg?: string | null;
  bgImgOpacity?: number;
  hideTitle?: boolean;
}

export default function InteractionWrapper({
  children,
  headingColor = '#FFFFFF',
  bgColor,
  title = '',
  updateTitle,
  bgImg,
  bgImgOpacity = 100,
  hideTitle = false,
}: InteractionWrapperProps) {
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const selectedSlide = useSelectedSlide();
  const disableAutoFocus = usePresentationUIStore(s => s.disableAutoFocus);
  const headingFontName = selectedSlide?.setting?.HeadingFont as SAFonts || 'Sans-Serif';
  const { getFontStyle } = useFonts();
  const headingFontStyle = getFontStyle(headingFontName);

  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate font size based on word count
  const calculateFontSize = (text: string) => {
    if (!text) return '1.75rem';

    const wordCount = text.trim().split(/\s+/).filter(word => word.length > 0).length;

    // Scaled down ~60% from live view to match visual appearance on smaller editor screen
    if (wordCount <= 30) {
      return '1.8rem'; // ~60% of text-5xl (3rem) - Up to 30 words
    } else if (wordCount <= 40) {
      return '1.38rem'; // ~60% of text-4xl (2.25rem) - 30-40 words
    } else {
      return '1.13rem'; // ~60% of text-3xl (1.875rem) - 40+ words
    }
  };

  const fontSize = calculateFontSize(title);

  return (
    <div
      ref={containerRef}
      style={{
        background: bgColor || 'black'
      }}
      className={'absolute w-full space-y-4 flex flex-col h-full overflow-y-auto px-8 pt-10 pb-4 text-md'}
    >
      {bgImg && <SlideBackground bgImg={bgImg} bgImgOpacity={bgImgOpacity / 100} />}
      {hideTitle ? (
        <div className="min-h-[2.5rem]" />
      ) : (
        <Textarea
          minRows={1}
          autoFocus={!disableAutoFocus}
          style={{
            ...headingFontStyle,
            color: headingColor,
            fontSize
          }}
          onFocusChange={(v) => setIsTitleFocused(v)}
          value={title}
          placeholder='Click to add heading'
          onChange={updateTitle}
          className="my-0! "
          classNames={{
            input: 'leading-tight font-semibold!',
          }}
          state={'presentationView'}
          maxLength={255}
        />
      )}
      <div className="size-full flex flex-col w-full h-full overflow-y-auto mt-0! pt-8 pb-4 z-2 items-center ">
        {children}
      </div>
    </div>
  );
}
